import { useState } from 'react';
import { Activity, Clock } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useUptimeHistory } from '@/hooks/uptime/use-uptime-history';
import type { UptimeRange } from '@/services/uptime/uptime.service';

interface UptimePanelProps {
  subscriptionId: string;
}

const RANGES: { value: UptimeRange; label: string }[] = [
  { value: '24h', label: '24 Hours' },
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
];

export function UptimePanel({ subscriptionId }: UptimePanelProps) {
  const [range, setRange] = useState<UptimeRange>('24h');
  const { data, isLoading, error } = useUptimeHistory(subscriptionId, range);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
              <Activity className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h2 className="text-2xl font-bold tracking-tight">Uptime</h2>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400 ml-12">
            Push-heartbeat tracking from the provider deployment
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
            <Clock className="h-4 w-4" />
            <span>Range</span>
          </div>
          <Select value={range} onValueChange={(v) => setRange(v as UptimeRange)}>
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGES.map((r) => (
                <SelectItem key={r.value} value={r.value}>
                  {r.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>Failed to load uptime history.</AlertDescription>
        </Alert>
      )}

      {isLoading || !data ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-slate-500">Status</CardTitle>
              </CardHeader>
              <CardContent>
                <Badge
                  variant="outline"
                  className={
                    data.currentStatus === 'UP'
                      ? 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300'
                      : 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/40 dark:text-red-300'
                  }
                >
                  {data.currentStatus}
                </Badge>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-slate-500">
                  Uptime ({range})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-semibold">
                  {data.uptimePct.toFixed(2)}%
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-slate-500">
                  Last heartbeat
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-sm">
                  {data.lastHeartbeatAt
                    ? formatDistanceToNow(new Date(data.lastHeartbeatAt), {
                        addSuffix: true,
                      })
                    : 'Never'}
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex w-full gap-[1px] h-10 rounded overflow-hidden bg-slate-100 dark:bg-slate-800">
                {data.buckets.map((b) => (
                  <div
                    key={b.ts}
                    title={`${new Date(b.ts).toLocaleString()} — ${b.up ? 'up' : 'down'}`}
                    className={`flex-1 ${
                      b.up
                        ? 'bg-emerald-500 dark:bg-emerald-600'
                        : 'bg-red-400 dark:bg-red-700'
                    }`}
                  />
                ))}
              </div>
              <div className="flex justify-between text-xs text-slate-500 mt-2">
                <span>{new Date(data.buckets[0]?.ts ?? Date.now()).toLocaleString()}</span>
                <span>now</span>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
